/**
 * SpotlightDirectory — public /spotlight page.
 *
 * Aggregated feed of live Sponsored Spotlight campaigns across every
 * Portalwiz employer who opted in (list_in_directory=true). Ranked by
 * view count by default. Supports:
 *   · Full-text search across name/headline/subheadline
 *   · Tag chip filter (facet counts from the backend)
 *   · Sort toggle: Trending (views) / Recently updated
 *
 * Unauthenticated — the endpoint is fully public.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Search, Flame, Clock, ArrowRight, Building2, X } from "lucide-react";

// Debounce hook — avoids firing a request on every keystroke.
function useDebounced(value, delay = 350) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

function TrendPill({ count }) {
    if (!count) return null;
    return (
        <span
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 bg-black text-white border border-black"
            data-testid="spotlight-directory-trend-pill"
        >
            <Flame className="w-3 h-3" />
            {count.toLocaleString()} views
        </span>
    );
}

function FeaturedBadge() {
    return (
        <span
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-gradient-to-r from-amber-400 to-amber-500 text-black border border-amber-600 shadow-sm"
            data-testid="spotlight-directory-featured-badge"
        >
            <Sparkles className="w-3 h-3" />
            Featured
        </span>
    );
}

function CampaignCard({ item }) {
    const bg = item.accent_color || "#002FA7";
    const to = item.public_path;
    if (!to) return null;
    return (
        <Link
            to={to}
            className={`group border bg-white transition-colors block overflow-hidden ${item.is_featured ? "border-amber-500 hover:border-amber-600 shadow-[0_0_0_1px_rgba(245,158,11,0.25)]" : "border-border hover:border-black"}`}
            data-testid={`spotlight-directory-card-${item.slug}`}
        >
            <div
                className="h-32 relative overflow-hidden"
                style={{
                    background: item.hero_image_url
                        ? `linear-gradient(135deg, ${bg}88, ${bg}22), url(${item.hero_image_url}) center/cover`
                        : `linear-gradient(135deg, ${bg}, #111)`,
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                {item.is_featured && (
                    <div className="absolute top-2 left-2">
                        <FeaturedBadge />
                    </div>
                )}
                <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between gap-2">
                    <div className="flex items-center gap-2 text-white/90 text-[10px] font-mono uppercase tracking-wider">
                        {item.company?.logo_url ? (
                            <img
                                src={item.company.logo_url}
                                alt={item.company.name}
                                className="w-5 h-5 bg-white p-0.5 border border-white/40 object-contain"
                            />
                        ) : (
                            <Building2 className="w-3.5 h-3.5" />
                        )}
                        <span className="truncate max-w-[140px]">{item.company?.name || "Employer"}</span>
                    </div>
                    <TrendPill count={item.view_count} />
                </div>
            </div>
            <div className="p-4 space-y-2">
                <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                    {item.name}
                </div>
                <h3 className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-black">
                    {item.headline}
                </h3>
                {item.subheadline ? (
                    <p className="text-xs text-muted-foreground line-clamp-2">{item.subheadline}</p>
                ) : null}
                {(item.directory_tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                        {item.directory_tags.slice(0, 4).map((t) => (
                            <span
                                key={t}
                                className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-border bg-neutral-50 text-neutral-700"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                )}
                <div className="pt-2 inline-flex items-center gap-1 text-[11px] font-medium text-black">
                    {item.cta_label || "View roles"} <ArrowRight className="w-3.5 h-3.5" />
                </div>
            </div>
        </Link>
    );
}

/** Larger "hero row" treatment for the top featured spotlights. Landscape
 *  card with bigger imagery, featured badge, and a stronger CTA. */
function FeaturedHeroCard({ item }) {
    const bg = item.accent_color || "#002FA7";
    const to = item.public_path;
    if (!to) return null;
    return (
        <Link
            to={to}
            className="group border-2 border-amber-500 bg-white block overflow-hidden hover:border-amber-600 transition-colors shadow-[0_1px_0_rgba(245,158,11,0.3)]"
            data-testid={`spotlight-directory-featured-card-${item.slug}`}
        >
            <div className="grid grid-cols-1 md:grid-cols-2">
                <div
                    className="h-40 md:h-full min-h-[180px] relative overflow-hidden"
                    style={{
                        background: item.hero_image_url
                            ? `linear-gradient(135deg, ${bg}77, ${bg}22), url(${item.hero_image_url}) center/cover`
                            : `linear-gradient(135deg, ${bg}, #111)`,
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-3 left-3"><FeaturedBadge /></div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                        <div className="flex items-center gap-2 text-white/90 text-[10px] font-mono uppercase tracking-wider">
                            {item.company?.logo_url ? (
                                <img src={item.company.logo_url} alt={item.company.name}
                                    className="w-6 h-6 bg-white p-0.5 border border-white/50 object-contain" />
                            ) : (
                                <Building2 className="w-4 h-4" />
                            )}
                            <span className="truncate max-w-[180px]">{item.company?.name || "Employer"}</span>
                        </div>
                        <TrendPill count={item.view_count} />
                    </div>
                </div>
                <div className="p-5 flex flex-col justify-between gap-3">
                    <div className="space-y-2">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                            {item.name}
                        </div>
                        <h3 className="text-lg font-semibold leading-snug line-clamp-2 group-hover:text-black">
                            {item.headline}
                        </h3>
                        {item.subheadline ? (
                            <p className="text-sm text-muted-foreground line-clamp-3">{item.subheadline}</p>
                        ) : null}
                        {(item.directory_tags || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1">
                                {item.directory_tags.slice(0, 5).map((t) => (
                                    <span key={t}
                                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-amber-200 bg-amber-50 text-amber-800">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-black">
                        {item.cta_label || "View roles"} <ArrowRight className="w-4 h-4" />
                    </div>
                </div>
            </div>
        </Link>
    );
}

export default function SpotlightDirectory() {
    const [items, setItems] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const search = useDebounced(searchInput, 350);
    const [selectedTag, setSelectedTag] = useState(null);
    const [sort, setSort] = useState("views"); // views | recent

    // Set OG / title tags for shareability.
    useEffect(() => {
        document.title = "Sponsored Spotlight Directory · Portalwiz";
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const params = { sort };
                if (search) params.search = search;
                if (selectedTag) params.tag = selectedTag;
                const { data } = await api.get("/public/spotlight-directory", { params });
                if (cancelled) return;
                setItems(data.items || []);
                setTags(data.tags || []);
            } catch (e) {
                if (!cancelled) setErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [search, selectedTag, sort]);

    const totalLabel = useMemo(() => {
        if (loading) return "Loading";
        if (items.length === 0) return "No campaigns match";
        if (items.length === 1) return "1 spotlight campaign";
        return `${items.length} spotlight campaigns`;
    }, [loading, items.length]);

    return (
        <div className="min-h-screen bg-white" data-testid="spotlight-directory-page">
            {/* Hero */}
            <div className="border-b border-border bg-gradient-to-b from-neutral-50 to-white">
                <div className="max-w-6xl mx-auto px-6 py-14">
                    <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground border border-border px-2 py-1 mb-6">
                        <Sparkles className="w-3 h-3" /> Sponsored Spotlight Directory
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">
                        Priority roles from teams hiring right now.
                    </h1>
                    <p className="text-base text-muted-foreground max-w-2xl mt-4">
                        Curated micro-sites from Portalwiz employers. Ranked by real-time interest — hop directly to any campaign to apply.
                    </p>
                    {/* Search bar */}
                    <div className="mt-8 flex items-center gap-3 max-w-xl">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by role, headline, keyword…"
                                className="pl-9 rounded-none border-black"
                                data-testid="spotlight-directory-search"
                            />
                            {searchInput && (
                                <button
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-black"
                                    onClick={() => setSearchInput("")}
                                    aria-label="Clear search"
                                    data-testid="spotlight-directory-search-clear"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        <div className="inline-flex border border-black">
                            <button
                                className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1 ${sort === "views" ? "bg-black text-white" : "bg-white text-black"}`}
                                onClick={() => setSort("views")}
                                data-testid="spotlight-directory-sort-views"
                            >
                                <Flame className="w-3.5 h-3.5" /> Trending
                            </button>
                            <button
                                className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1 border-l border-black ${sort === "recent" ? "bg-black text-white" : "bg-white text-black"}`}
                                onClick={() => setSort("recent")}
                                data-testid="spotlight-directory-sort-recent"
                            >
                                <Clock className="w-3.5 h-3.5" /> Recent
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag facets */}
            {tags.length > 0 && (
                <div className="border-b border-border bg-white sticky top-0 z-10">
                    <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground pr-2 shrink-0">Filter</span>
                        <button
                            className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border ${selectedTag === null ? "bg-black text-white border-black" : "bg-white text-black border-border hover:border-black"}`}
                            onClick={() => setSelectedTag(null)}
                            data-testid="spotlight-directory-tag-all"
                        >
                            All
                        </button>
                        {tags.map((t) => (
                            <button
                                key={t.tag}
                                className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border shrink-0 ${selectedTag === t.tag ? "bg-black text-white border-black" : "bg-white text-black border-border hover:border-black"}`}
                                onClick={() => setSelectedTag(t.tag)}
                                data-testid={`spotlight-directory-tag-${t.tag}`}
                            >
                                {t.tag} <span className="opacity-60">·{t.count}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Grid */}
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex items-center justify-between mb-6">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="spotlight-directory-count">
                        {totalLabel}
                    </div>
                </div>

                {err && (
                    <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-4" data-testid="spotlight-directory-error">
                        {err}
                    </div>
                )}

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="spotlight-directory-loading">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="border border-border h-72 bg-neutral-50 animate-pulse" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="border border-border p-12 text-center" data-testid="spotlight-directory-empty">
                        <Sparkles className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
                        <div className="text-sm font-medium">No campaigns match your filters.</div>
                        <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto">
                            Try clearing the search / tag, or check back soon — employers publish new Priority Roles every week.
                        </p>
                        {(search || selectedTag) && (
                            <Button
                                variant="outline"
                                className="mt-4 rounded-none"
                                onClick={() => { setSearchInput(""); setSelectedTag(null); }}
                                data-testid="spotlight-directory-reset"
                            >
                                Reset filters
                            </Button>
                        )}
                    </div>
                ) : (
                    (() => {
                        // Split into up-to-2 hero-row featured + everything else.
                        // Overflow featured campaigns keep their FEATURED badge
                        // when they slot back into the standard grid.
                        const featured = items.filter((i) => i.is_featured);
                        const heroSlots = featured.slice(0, 2);
                        const heroIds = new Set(heroSlots.map((i) => i.id));
                        const rest = items.filter((i) => !heroIds.has(i.id));
                        return (
                            <div className="space-y-8">
                                {heroSlots.length > 0 && (
                                    <div data-testid="spotlight-directory-featured-row">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">
                                                Featured spotlights
                                            </div>
                                            <div className="h-px flex-1 bg-gradient-to-r from-amber-400/60 to-transparent" />
                                        </div>
                                        <div className={`grid gap-5 ${heroSlots.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                                            {heroSlots.map((it) => (
                                                <FeaturedHeroCard key={it.id} item={it} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {rest.length > 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="spotlight-directory-grid">
                                        {rest.map((it) => (
                                            <CampaignCard key={it.id} item={it} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })()
                )}
            </div>

            {/* Footer CTA — employer acquisition */}
            <div className="border-t border-border bg-neutral-50">
                <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">For employers</div>
                        <h2 className="text-lg font-semibold mt-1">Get your roles listed here.</h2>
                        <p className="text-xs text-muted-foreground mt-1 max-w-md">
                            Spin up a branded Spotlight micro-site in minutes. Toggle&nbsp;
                            <span className="font-mono">List in Directory</span> to appear on this page.
                        </p>
                    </div>
                    <Link
                        to="/pricing/spotlight"
                        className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-xs font-medium hover:bg-neutral-800"
                        data-testid="spotlight-directory-cta-pricing"
                    >
                        See pricing <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
